import { useMemo } from "react";
import * as Yup from "yup";
import fetchJSON from "helpers/restApiClient";
import {
  ADDRESS_MAX_LENGTH,
  GRADE_DISPLAY_PREFIX,
  GRADE_NAME_PREFIX,
  INDIA_COUNTRY_NAME,
  INDIA_POSTAL_CODE_LENGTH,
  INDIA_POSTAL_CODE_PATTERN,
  MAX_GRADE_COUNT_INPUT_LENGTH,
  MAX_STUDENT_COUNT,
  MIN_POC_SEARCH_LENGTH,
  NON_INDIA_UDISE_CODE,
  SCHOOL_API_DEFAULT_ERROR_MESSAGE,
  SCHOOL_API_FAILURE_STATUS,
  SCHOOL_API_HEADER_VALUES,
  SCHOOL_API_PAYLOAD_FIELDS,
  SCHOOL_API_RESPONSE_FIELDS,
  SCHOOL_BOARD_FIELDS,
  SCHOOL_MDS_RESOURCE_TYPES,
  SCHOOL_CODE_PATTERN,
  SEARCH_SCHOOL_BY_NAME,
  SEARCH_SCHOOL_BY_SCHOOL_CODE,
  SEARCH_SCHOOL_BY_UDISE_CODE,
  SELECTABLE_COUNTRY_ALIASES,
  STATE_DISTRICT_FIELDS,
  TOKEN_ENV_MAP,
  UAE_COUNTRY_ALIASES,
  UDISE_CODE_PATTERN,
  UDISE_CREATE_FORM_MAP,
  formatSchoolType,
  isSchoolNameBlank,
  sanitizeUdiseCode,
  SCHOOL_ACCESS_BY_ENV,
  SCHOOL_POC_ROLE_IDS_BY_ENV,
} from "constants/schoolConstants";
import {
  REACT_APP_AUTHENTICATE_TOKEN_NAME,
  DEFAULT_PRODUCT_ID,
  DEFAULT_TENANT_ID,
  ZERO,
  ONE,
  SEARCH_VALIDATION_MESSAGES,
  MIN_SCHOOL_NAME_SEARCH_LENGTH,
} from "constants/constants";
import { tokenUtils } from "utils";
import { shouldShowFormError } from "utils/utils";
import {
  useFetchBoardList,
  useFetchCityList,
  useFetchCountryList,
  useFetchGradeList,
  useFetchPostalCodeDetails,
  useFetchTierList,
  useSearchExecutiveAgents,
  useSearchSchoolPocAgents,
} from "store/backend/apiHooks";

export const getSchoolApiErrorMessage = (
  error,
  fallback = SCHOOL_API_DEFAULT_ERROR_MESSAGE,
) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  if (typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }
  const data = error.data;
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (typeof data?.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  return fallback;
};

export const getSchoolApiErrorCode = (error) =>
  error?.errorCode || error?.data?.error_code || error?.data?.errorCode || null;

const normalizeSchoolApiError = (error) => ({
  ...error,
  message: getSchoolApiErrorMessage(error),
  errorCode: getSchoolApiErrorCode(error),
});

export const assertSchoolApiSuccess = (response) => {
  if (response?.status === SCHOOL_API_FAILURE_STATUS) {
    throw normalizeSchoolApiError({
      statusCode: response.statusCode,
      statusText: response.statusText,
      message: response.message,
      errorCode: response.errorCode,
      data: response.data,
    });
  }
  return response;
};

export const callSchoolApi = async (url, options) => {
  try {
    return assertSchoolApiSuccess(await fetchJSON(url, options));
  } catch (error) {
    throw normalizeSchoolApiError(error);
  }
};

export const buildSchoolApiHeaders = (token) => ({
  accept: SCHOOL_API_HEADER_VALUES.ACCEPT_JSON,
  platform: SCHOOL_API_HEADER_VALUES.PLATFORM_WEB,
  Authorization: `Bearer ${token}`,
});

export const buildSchoolGatewayAgentHeaders = (token) => ({
  accept: SCHOOL_API_HEADER_VALUES.ACCEPT_JSON,
  "product-id": DEFAULT_PRODUCT_ID,
  "tenant-id": DEFAULT_TENANT_ID,
  platform: SCHOOL_API_HEADER_VALUES.PLATFORM_WEB,
  Authorization: `Bearer ${token}`,
  "Content-Type": SCHOOL_API_HEADER_VALUES.ACCEPT_JSON,
});

const normalizeRoleId = (role) => {
  const roleId = Number(role);
  return Number.isNaN(roleId) ? null : roleId;
};

const getUserRoleIds = () =>
  tokenUtils
    .getLoginRoles()
    .map(normalizeRoleId)
    .filter((roleId) => roleId !== null);

const getAppEnvironment = () =>
  process.env.REACT_APP_APP_ENV ||
  TOKEN_ENV_MAP[REACT_APP_AUTHENTICATE_TOKEN_NAME]

const getSchoolAccessConfig = () =>
  SCHOOL_ACCESS_BY_ENV[getAppEnvironment()] || SCHOOL_ACCESS_BY_ENV.staging;

const hasAnyRole = (userRoleIds, allowedRoleIds) => {
  const allowed = new Set(allowedRoleIds);
  return userRoleIds.some((roleId) => allowed.has(roleId));
};

export const canCreateSchool = () => {
  const { fullAccessRoleIds, createOnlyRoleIds } = getSchoolAccessConfig();
  const roles = getUserRoleIds();
  return (
    hasAnyRole(roles, fullAccessRoleIds) || hasAnyRole(roles, createOnlyRoleIds)
  );
};

export const canEditSchool = () =>
  hasAnyRole(getUserRoleIds(), getSchoolAccessConfig().fullAccessRoleIds);

export const getSchoolPocRoleIds = () =>
  SCHOOL_POC_ROLE_IDS_BY_ENV[getAppEnvironment()]

export const getExecutiveRoleIds = () => {
  const { fullAccessRoleIds, createOnlyRoleIds } = getSchoolAccessConfig();
  return [...new Set([...fullAccessRoleIds, ...createOnlyRoleIds])];
};

export const idsMatch = (a, b) => {
  if (a == null || a === "" || b == null || b === "") {
    return (a == null || a === "") && (b == null || b === "");
  }
  return Number(a) === Number(b);
};

export const normalizeFormId = (id) => {
  if (id == null || id === "") return "";
  const num = Number(id);
  return Number.isNaN(num) ? id : num;
};

export const findMappingItemById = (items, id) => {
  if (!Array.isArray(items) || id == null || id === "") return null;
  return items.find((item) => idsMatch(item.id, id)) ?? null;
};

export const getSelectedCountryOption = (countryOptions, countryId) =>
  countryOptions.find((option) => idsMatch(option.value, countryId)) ?? null;

export const getSchoolSearchSchema = (searchBy) => {
  if (searchBy === SEARCH_SCHOOL_BY_UDISE_CODE) {
    return Yup.string().matches(
      UDISE_CODE_PATTERN,
      SEARCH_VALIDATION_MESSAGES.UDISE_INVALID,
    );
  }
  if (searchBy === SEARCH_SCHOOL_BY_SCHOOL_CODE) {
    return Yup.string().matches(
      SCHOOL_CODE_PATTERN,
      SEARCH_VALIDATION_MESSAGES.SCHOOL_CODE_INVALID,
    );
  }
  if (searchBy === SEARCH_SCHOOL_BY_NAME) {
    return Yup.string().min(
      MIN_SCHOOL_NAME_SEARCH_LENGTH,
      SEARCH_VALIDATION_MESSAGES.SCHOOL_NAME_MIN_LENGTH,
    );
  }
  return null;
};

export const getSchoolSearchError = (searchBy, value) => {
  const schema = getSchoolSearchSchema(searchBy);
  if (!schema) return null;
  const rawValue = String(value || "");
  if (!rawValue) return null;
  const trimmedValue = rawValue.trim();
  if (!trimmedValue) {
    return SEARCH_VALIDATION_MESSAGES.SEARCH_VALUE_BLANK;
  }
  try {
    schema.validateSync(trimmedValue);
    return null;
  } catch (error) {
    return error.message;
  }
};

export const isSchoolSearchEnabled = (searchBy, value) => {
  if (!getSchoolSearchSchema(searchBy)) return false;
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) return false;
  return !getSchoolSearchError(searchBy, value);
};

export const isSelectableCountry = (displayName) => {
  const normalized = (displayName || "").trim().toLowerCase();
  return SELECTABLE_COUNTRY_ALIASES.includes(normalized);
};

export const isIndiaCountry = (displayName) =>
  (displayName || "").trim().toLowerCase() === INDIA_COUNTRY_NAME;

export const isUaeCountry = (displayName) => {
  const normalized = (displayName || "").trim().toLowerCase();
  return UAE_COUNTRY_ALIASES.includes(normalized);
};

export const isIndiaCountryId = (countryOptions, countryId) =>
  isIndiaCountry(getSelectedCountryOption(countryOptions, countryId)?.label);

export const normalizeUdiseCodeForForm = (udiseCode, isIndia) => {
  const trimmed = String(udiseCode ?? "").trim();
  if (!isIndia) return "";
  if (!trimmed || trimmed.toLowerCase() === NON_INDIA_UDISE_CODE) return "";
  return trimmed;
};

const toNonNegativeInt = (value) => {
  if (value == null || value === "") return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < ZERO) return null;
  return Math.trunc(numericValue);
};

export const normalizeFeeForForm = (fee) => toNonNegativeInt(fee) ?? "";

const getByPath = (obj, path) => path.reduce((current, key) => current?.[key], obj);

const unwrapUdiseSchool = (udiseSchool) => {
  if (udiseSchool?.data && typeof udiseSchool.data === "object") {
    return udiseSchool.data;
  }
  return udiseSchool || {};
};

const normalizePrefillFieldValue = (formFieldName, fieldValue) => {
  const fieldKind = UDISE_CREATE_FORM_MAP.find(
    ({ formKey }) => formKey === formFieldName,
  )?.kind;
  if (fieldKind === "int") return toNonNegativeInt(fieldValue);
  return String(fieldValue ?? "").trim() || null;
};

const applyNormalizedPrefillField = (target, formFieldName, fieldValue) => {
  if (fieldValue == null || fieldValue === "") return;
  const normalizedValue = normalizePrefillFieldValue(formFieldName, fieldValue);
  if (normalizedValue != null && normalizedValue !== "") {
    target[formFieldName] = normalizedValue;
  }
};

export const buildCreateSchoolPrefill = ({
  countryId,
  udiseCode: enteredUdiseCode,
  udiseSchool,
}) => {
  const school = unwrapUdiseSchool(udiseSchool);
  const values = {
    countryId: normalizeFormId(countryId) || "",
    udiseCode:
      sanitizeUdiseCode(school.udiseCode) ||
      sanitizeUdiseCode(enteredUdiseCode),
  };

  UDISE_CREATE_FORM_MAP.forEach(({ formKey, path }) => {
    applyNormalizedPrefillField(values, formKey, getByPath(school, path));
  });

  return values;
};

export const clearStateDistrictFormFields = (setFieldValue) => {
  STATE_DISTRICT_FIELDS.forEach((field) => setFieldValue(field, "", false));
};

export const filterCityOptionsForCountry = (
  allCityOptions,
  countryId,
  selectedCityId = "",
) => {
  const filtered = countryId
    ? allCityOptions.filter((city) => idsMatch(city.countryId, countryId))
    : [];
  if (!selectedCityId) return filtered;
  const selectedCity = allCityOptions.find((city) =>
    idsMatch(city.value, selectedCityId),
  );
  if (
    selectedCity &&
    !filtered.some((city) => idsMatch(city.value, selectedCity.value))
  ) {
    return [selectedCity, ...filtered];
  }
  return filtered;
};

export const getBoardDisplayState = (
  boardIds,
  boardsLookup,
  isBoardListLoading,
) => {
  const ids = boardIds || [];
  const labels = ids
    .map((boardId) => boardsLookup[boardId]?.value)
    .filter(Boolean);
  const isLoading =
    isBoardListLoading ||
    ids.some((boardId) => boardsLookup[boardId]?.isLoading);
  return { labels, isLoading };
};

export const normalizeBoardIdList = (ids) =>
  (ids || [])
    .map((id) => normalizeFormId(id))
    .filter((id) => id !== "");

export const toBoardIdsPayload = (ids) => normalizeBoardIdList(ids).map(Number);

export const findGradeRowIndex = (rows, gradeId) =>
  rows.findIndex((row) => String(row.gradeId) === String(gradeId));

export const sanitizeGradeCountInput = (rawValue) =>
  String(rawValue || "")
    .replace(/\D/g, "")
    .slice(0, MAX_GRADE_COUNT_INPUT_LENGTH);

export const getSelectFieldErrorState = ({
  touched,
  errors,
  field,
  isFetchError,
  submitAttempted,
}) => ({
  yupError: shouldShowFormError(touched[field], errors[field], submitAttempted),
  fetchError:
    shouldShowFormError(touched[field], true, submitAttempted) &&
    isFetchError &&
    !errors[field],
});

export const resetSchoolAddressForCountryChange = ({
  newCountryId,
  countryOptions,
  setFieldValue,
  setFieldTouched,
  setFieldError,
}) => {
  setFieldValue("cityId", "", true);
  setFieldValue("tierId", "", true);
  clearStateDistrictFormFields(setFieldValue);
  setFieldValue("postalCode", "", true);
  setFieldValue("udiseCode", "", true);
  if (
    isIndiaCountry(
      getSelectedCountryOption(countryOptions, newCountryId)?.label,
    )
  ) {
    setFieldValue("area", "", true);
    return;
  }
  STATE_DISTRICT_FIELDS.forEach((field) => {
    setFieldTouched(field, false, false);
    setFieldError(field, undefined);
  });
};

const getValidGradeRows = (rows) =>
  (rows || []).filter(
    (row) =>
      row.studentCount !== "" &&
      row.studentCount != null &&
      !Number.isNaN(Number(row.studentCount)),
  );
const optionalTrimmed = (value) => (value?.trim() ? value.trim() : null);
const optionalNumber = (value) => (value ? Number(value) : null);

export const buildSchoolPayload = (values, countryOptions = []) => {
  const {
    schoolName,
    udiseCode,
    schoolType,
    address,
    countryId,
    cityId,
    stateId,
    district,
    area,
    postalCode,
    totalStudentCount,
    totalTeacherCount,
    fee,
    tierId,
    gradewiseStudentCount,
    pocId,
    executiveId,
  } = values;
  const isIndia = isIndiaCountryId(countryOptions, countryId);
  const payloadFields = SCHOOL_API_PAYLOAD_FIELDS;
  const payload = {
    [payloadFields.SCHOOL_NAME]: schoolName.trim(),
    [payloadFields.SCHOOL_TYPE]: schoolType,
    [payloadFields.ADDRESS]: address,
    [payloadFields.COUNTRY_ID]: Number(countryId),
    [payloadFields.CITY_ID]: Number(cityId),
    [payloadFields.STATE_ID]: isIndia && stateId ? Number(stateId) : null,
    [payloadFields.DISTRICT_NAME]: isIndia ? optionalTrimmed(district) : null,
    [payloadFields.AREA]: !isIndia ? optionalTrimmed(area) : null,
    [payloadFields.POSTAL_CODE]: optionalTrimmed(postalCode),
    ...Object.fromEntries(
      SCHOOL_BOARD_FIELDS.map(({ name, payloadKey }) => [
        payloadKey,
        toBoardIdsPayload(values[name]),
      ]),
    ),
    [payloadFields.FEE]: Number(fee),
    [payloadFields.TIER_ID]: Number(tierId),
    [payloadFields.TOTAL_STUDENT_COUNT]: Number(totalStudentCount),
    [payloadFields.TOTAL_TEACHER_COUNT]: optionalNumber(totalTeacherCount),
    [payloadFields.POC_ID]: pocId ? Number(pocId) : null,
    [payloadFields.EXECUTIVE_ID]: executiveId ? Number(executiveId) : null,
    [payloadFields.GRADE_WISE_STUDENT_COUNT]: getValidGradeRows(
      gradewiseStudentCount,
    ).map((row) => ({
      [payloadFields.GRADE_ID]: Number(row.gradeId),
      [payloadFields.STUDENT_COUNT]: Number(row.studentCount),
    })),
  };
  const trimmedUdise = udiseCode?.trim();
  if (isIndia && trimmedUdise) payload[payloadFields.UDISE_CODE] = trimmedUdise;
  return payload;
};

export const isEditUdiseCodeLocked = (schoolDetails, values) => {
  const originalUdise = normalizeUdiseCodeForForm(
    schoolDetails?.udiseCode,
    true,
  );
  if (!originalUdise) return false;
  return String(values?.udiseCode ?? "").trim() === originalUdise;
};

export const isCreateSchoolNameLocked = (createSchoolPrefill) =>
  !isSchoolNameBlank(createSchoolPrefill?.schoolName);

export const hasRegisteredSchoolForUdise = (searchResponse) => {
  if(!searchResponse) return false;
  if (searchResponse.statusCode !== 200 || !searchResponse.data) return false;
  const schools = searchResponse.data[SCHOOL_API_RESPONSE_FIELDS.SCHOOLS];
  const totalCount = searchResponse.data[SCHOOL_API_RESPONSE_FIELDS.TOTAL_COUNT];
  if (Array.isArray(schools) && schools.length > ZERO) return true;
  return Number(totalCount) > ZERO;
};

export const isCreateSchoolUdiseLocked = (createSchoolPrefill) =>
  Boolean(sanitizeUdiseCode(createSchoolPrefill?.udiseCode));

export const buildSchoolUpdatePayload = (
  values,
  schoolDetails,
  countryOptions = [],
) => {
  const countryId =
    values.countryId != null && values.countryId !== ""
      ? values.countryId
      : schoolDetails?.countryId;
  return {
    ...buildSchoolPayload({ ...values, countryId }, countryOptions),
    [SCHOOL_API_PAYLOAD_FIELDS.COUNTRY_ID]: Number(countryId),
  };
};

export const buildSchoolStatusUpdatePayload = (countryId, status) => ({
  [SCHOOL_API_PAYLOAD_FIELDS.COUNTRY_ID]: Number(countryId),
  [SCHOOL_API_PAYLOAD_FIELDS.STATUS]: status,
});

export const createSchoolInitialValues = {
  schoolName: "",
  udiseCode: "",
  schoolType: "",
  address: "",
  countryId: "",
  cityId: "",
  state: "",
  district: "",
  stateId: "",
  districtId: "",
  area: "",
  postalCode: "",
  ...Object.fromEntries(SCHOOL_BOARD_FIELDS.map(({ name }) => [name, []])),
  totalStudentCount: "",
  totalTeacherCount: "",
  fee: "",
  tierId: "",
  gradewiseStudentCount: [],
  pocId: "",
  executiveId: "",
};

export const mergeCreateSchoolPrefill = (
  createSchoolPrefill = {},
  countryOptions = [],
) => {
  const countryId = normalizeFormId(createSchoolPrefill?.countryId);
  const isIndia = isIndiaCountryId(countryOptions, countryId);
  const createSchoolFormValues = {
    ...createSchoolInitialValues,
    countryId: countryId || "",
    udiseCode: normalizeUdiseCodeForForm(
      createSchoolPrefill?.udiseCode,
      isIndia,
    ),
  };

  UDISE_CREATE_FORM_MAP.forEach(({ formKey }) => {
    applyNormalizedPrefillField(
      createSchoolFormValues,
      formKey,
      createSchoolPrefill?.[formKey],
    );
  });

  return createSchoolFormValues;
};

const isValidFormId = (value) =>
  value != null && value !== "" && !Number.isNaN(Number(value));

const boardIdsFieldSchema = ({ ofNumber = false } = {}) =>
  Yup.array()
    .of(
      ofNumber
        ? Yup.number()
        : Yup.mixed().test("is-board-id", "Invalid board", isValidFormId),
    )
    .min(ONE, "At least one board is required");

const schoolBoardIdsSchemaFields = ({ ofNumber = false } = {}) =>
  Object.fromEntries(
    SCHOOL_BOARD_FIELDS.map(({ name }) => [
      name,
      boardIdsFieldSchema({ ofNumber }),
    ]),
  );

const nullableNumberField = () =>
  Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue == null ? null : value,
    );
const IndiaOnlyValidation = (countryOptions, fn) =>
  function (value) {
    if (!isIndiaCountryId(countryOptions, this.parent.countryId)) return true;
    return fn.call(this, value);
  };

const indiaOnlyId = (countryOptions, message) =>
  Yup.mixed().test(
    "india-only-id",
    message,
    IndiaOnlyValidation(countryOptions, isValidFormId),
  );

const indiaAddressSchemaFields = (countryOptions, mode = "create") => {
  const IndiaOnlyTextValidation = (message, idField) =>
    Yup.string().test(
      `${idField}-required`,
      message,
      IndiaOnlyValidation(countryOptions, function (value) {
        return !!value || isValidFormId(this.parent[idField]);
      }),
    );
  const IndiaOnlyIdOrTextValidation = (message, textField) =>
    Yup.mixed().test(
      `${textField}-id-required`,
      message,
      IndiaOnlyValidation(countryOptions, function (value) {
        return (
          isValidFormId(value) || !!String(this.parent[textField] || "").trim()
        );
      }),
    );
  if (mode === "create") {
    const required = (message) =>
      Yup.string().test(
        "required",
        message,
        IndiaOnlyValidation(countryOptions, (v) => !!v),
      );
    return {
      state: required("State is required"),
      stateId: indiaOnlyId(countryOptions, "State is required"),
      district: required("District is required"),
      districtId: indiaOnlyId(countryOptions, "District is required"),
    };
  }
  return {
    state: IndiaOnlyTextValidation("State is required", "stateId"),
    stateId: IndiaOnlyIdOrTextValidation("State is required", "state"),
    district: IndiaOnlyTextValidation("District is required", "districtId"),
    districtId: IndiaOnlyIdOrTextValidation("District is required", "district"),
  };
};

export const createSchoolSchema = (countryOptions) => {
  const isIndia = (countryId) => isIndiaCountryId(countryOptions, countryId);
  const country = (countryId) =>
    getSelectedCountryOption(countryOptions, countryId);
  return Yup.object().shape({
    schoolName: Yup.string()
      .required("School name is required")
      .test(
        "not-blank",
        "School name cannot be only spaces",
        (v) => !isSchoolNameBlank(v),
      ),
    udiseCode: Yup.string()
      .nullable()
      .notRequired()
      .test("udise-code-validation", "Invalid UDISE code", function (value) {
        if (!isIndia(this.parent.countryId)) return true;
        const trimmed = value?.trim();
        if (!trimmed)
          return this.createError({ message: "UDISE code is required" });
        if (!UDISE_CODE_PATTERN.test(trimmed)) {
          return this.createError({
            message: "UDISE code must be exactly 11 digits",
          });
        }
        return true;
      }),
    schoolType: Yup.string().required("School type is required"),
    address: Yup.string()
      .required("Address is required")
      .max(ADDRESS_MAX_LENGTH, `Address must be at most ${ADDRESS_MAX_LENGTH} characters`),
    countryId: Yup.mixed()
      .required("Country is required")
      .test("country-selected", "Please select a country", isValidFormId)
      .test("country-valid", "Please select a valid country", function (value) {
        if (!isValidFormId(value)) return true;
        return countryOptions.some((option) => idsMatch(option.value, value));
      }),
    cityId: Yup.mixed()
      .required("City is required")
      .test("city-selected", "Please select a city", isValidFormId),
    ...indiaAddressSchemaFields(countryOptions, "create"),
    area: Yup.string()
      .nullable()
      .test("area-required", "Area is required", function (value) {
        if (!this.parent.countryId || isIndia(this.parent.countryId))
          return true;
        return !!String(value || "").trim();
      }),
    postalCode: Yup.string().test(
      "postal-code-validation",
      "Invalid postal code",
      function (value) {
        const selectedIsIndia = isIndia(this.parent.countryId);
        const selectedIsUae = isUaeCountry(
          country(this.parent.countryId)?.label,
        );
        if (selectedIsIndia) {
          if (!value)
            return this.createError({ message: "Postal code is required" });
          if (!INDIA_POSTAL_CODE_PATTERN.test(value)) {
            return this.createError({ message: "Invalid postal code" });
          }
        }
        if (selectedIsUae && value && !/^\d+$/.test(value)) {
          return this.createError({ message: "Invalid postal code" });
        }
        return true;
      },
    ),
    ...schoolBoardIdsSchemaFields({ ofNumber: true }),
    totalStudentCount: Yup.number()
      .typeError("Total student count must be a number")
      .required("Total student count is required")
      .positive("Total student count must be a positive number")
      .test(
        "sum-match",
        "Total student count must be greater than or equal to the sum of grade-wise student counts",
        function (value) {
          const rows = getValidGradeRows(this.parent.gradewiseStudentCount);
          if (rows.length === ZERO) return true;
          const sum = rows.reduce(
            (total, row) => total + (Number(row.studentCount) || ZERO),
            ZERO,
          );
          return Number(value) >= sum;
        },
      ),
    totalTeacherCount: nullableNumberField()
      .positive("Total teacher count must be a positive number")
      .notRequired(),
    fee: Yup.number()
      .typeError("Annual school fee must be a number")
      .required("Annual school fee is required")
      .integer("Annual school fee must be a whole number")
      .min(ZERO, "Annual school fee cannot be negative"),
    tierId: Yup.mixed()
      .required("Tier is required")
      .test("tier-selected", "Please select a tier", isValidFormId),
    gradewiseStudentCount: Yup.array().of(
      Yup.object().shape({
        gradeId: Yup.mixed(),
        studentCount: nullableNumberField()
          .typeError("Student count must be a number")
          .min(ZERO, "Student count cannot be negative")
          .max(MAX_STUDENT_COUNT, "Student count is too large")
          .notRequired(),
      }),
    ),
    pocId: Yup.mixed()
      .required("School POC is required")
      .test("poc-selected", "Please select a School POC", isValidFormId),
    executiveId: Yup.mixed()
      .required("Genius Execution Rep is required")
      .test(
        "executive-id-selected",
        "Please select a Genius Execution Rep",
        isValidFormId,
      ),
  });
};

export const createSchoolEditSchema = (countryOptions) =>
  createSchoolSchema(countryOptions).shape({
    countryId: Yup.mixed()
      .required("Country is required")
      .test("country-selected", "Please select a country", isValidFormId),
    ...indiaAddressSchemaFields(countryOptions, "edit"),
    ...schoolBoardIdsSchemaFields({ ofNumber: false }),
  });


const pickAgentField = (agent, field, fallback = "") => {
  if (!agent) return fallback;
  if (agent[field] != null) return agent[field];
  return fallback;
};

export const normalizeAgent = (agent) => {
  const source = agent ? agent : {};
  return {
    uamId: pickAgentField(source, "uamId", null),
    firstName: pickAgentField(source, "firstName"),
    lastName: pickAgentField(source, "lastName"),
    phone: pickAgentField(source, "phone"),
    email: pickAgentField(source, "email", null),
    roles: source.roles != null ? source.roles : [],
    crnId: pickAgentField(source, "crnId", null),
    employeeCode: pickAgentField(source, "employeeCode"),
  };
};

export const getSchoolAgentUserIds = (schoolDetails) => {
  if (!schoolDetails) return [];
  const ids = [];
  if (schoolDetails.pocId) ids.push(schoolDetails.pocId);
  if (schoolDetails.executiveId) ids.push(schoolDetails.executiveId);
  return ids;
};

const isAgentRecord = (value) =>
  value != null &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  value.uamId != null;

export const getAgentsFromResponse = (response) => {
  if (!response) return [];
  if (Array.isArray(response.data)) {
    return response.data.map(normalizeAgent);
  }
  const agents = response.data?.agents ?? response.agents;
  if (Array.isArray(agents)) return agents.map(normalizeAgent);
  if (isAgentRecord(response.data)) return [normalizeAgent(response.data)];
  return [];
};

export const normalizeAgentsResponse = (response) => {
  const agents = getAgentsFromResponse(response);
  const data =
    response?.data != null &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
      ? { ...response.data, agents }
      : { agents };
  return {
    ...response,
    data,
  };
};

export const getAgentByUamId = (response, uamId) => {
  if (uamId == null || uamId === "") return undefined;
  return getAgentsFromResponse(response).find(
    (agent) => Number(agent.uamId) === Number(uamId),
  );
};


export const getAgentNameSearchTerm = (value) => {
  if (value == null) return "";
  const trimmedValue = String(value).trim();
  if (!trimmedValue) return "";
  return trimmedValue.replace(/\s*\([^)]*\)\s*$/, "").trim();
};

export const getAgentLabelByIdentifierField = (agent, identifierField) => {
  if (!agent) return null;
  const name = [agent.firstName, agent.lastName].filter(Boolean).join(" ");
  const identifierText = String(agent[identifierField] ?? "").trim();
  if (!identifierText) return name;
  return `${name} (${identifierText})`;
};

const toAgentSelectOption = (agent, identifierField) => {
  if (!agent) return null;
  if (agent.uamId == null) return null;
  return {
    value: agent.uamId,
    label: getAgentLabelByIdentifierField(agent, identifierField),
  };
};

const mapAgentsToSelectOptions = (response, identifierField) =>
  getAgentsFromResponse(response)
    .map((agent) => toAgentSelectOption(agent, identifierField))
    .filter((option) => option != null);

const readDisplayName = (details) =>
  details?.displayName ?? details?.display_name ?? null;
const normalizeDetailsMappingItem = (item, extra = {}) => {
  if (!item?.details) return item;
  const { details } = item;
  return {
    id: item.id,
    name: details.name,
    displayName: readDisplayName(details),
    ...extra(details, item),
  };
};

export const normalizeCountryMappingItem = (item) =>
  normalizeDetailsMappingItem(item, (details) => ({
    code: details.code,
    currencies: details.currencies,
    timezones: details.timezones,
    isdCodes: details.isdCodes ?? details.isd_codes,
  }));
export const normalizeCityMappingItem = (item) => {
  if (!item?.details) return item;
  return {
    id: item.id,
    name: item.details.name,
    displayName: item.details.displayName,
    countryId: item.details.country?.id,
    tierId: item.details.cityTier?.id,
  };
};

export const normalizeTierMappingItem = (item) =>
  normalizeDetailsMappingItem(item, () => ({}));

export const normalizeMdsMappingListResponse = (response, normalizeItem) =>
  response?.items
    ? { ...response, items: response.items.map(normalizeItem) }
    : response;

export const getMdsResourceLabel = (resourceType, response) => {
  if (!response || response.detail) return null;
  if (resourceType === SCHOOL_MDS_RESOURCE_TYPES.GRADE) {
    const name = response.name ?? response.displayName;
    if (!name) return null;
    const normalized = String(name);
    return normalized.toLowerCase().startsWith(GRADE_NAME_PREFIX)
      ? normalized
      : `${GRADE_DISPLAY_PREFIX}${normalized}`;
  }
  const details = response.details;
  return (
    response.displayName ||
    response.display_name ||
    details?.displayName ||
    details?.display_name ||
    response.name ||
    details?.name ||
    null
  );
};

const readSchoolDetailText = (schoolDetails, ...keys) => {
  for (const key of keys) {
    const value = schoolDetails?.[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return null;
};

const resolveFormId = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate != null && candidate !== "")
      return normalizeFormId(candidate);
  }
  return "";
};

const optionalFormId = (id) =>
  id != null && id !== "" ? normalizeFormId(id) : "";
const getSchoolCountryDisplayName = (schoolDetails, countryItems) =>
  findMappingItemById(countryItems, schoolDetails?.countryId)?.displayName ??
  null;
export const shouldFetchSchoolPostalCode = (schoolDetails, countryItems) =>
  isIndiaCountry(getSchoolCountryDisplayName(schoolDetails, countryItems)) &&
  String(schoolDetails?.postalCode ?? "").trim().length ===
    INDIA_POSTAL_CODE_LENGTH;
const lookupState = (isLoading, isError, apiValue, resolvedValue) =>
  isLoading
    ? { value: null, isLoading: true }
    : {
        value: (isError ? apiValue : resolvedValue || apiValue) ?? null,
        isLoading: false,
      };
const resolvePostalId = (isLoading, isError, postalId, fallbackId) =>
  isLoading
    ? null
    : isError
      ? (fallbackId ?? null)
      : (postalId ?? fallbackId ?? null);
export const buildSchoolStateDistrictLookups = ({
  schoolDetails,
  shouldFetchPostal,
  postalResponse,
  isPostalLoading = false,
  isPostalFetching = false,
  isPostalError = false,
  includeIds = false,
}) => {
  const apiStateName = readSchoolDetailText(
    schoolDetails,
    "stateName",
    "state_name",
    "state",
  );
  const apiDistrictName = readSchoolDetailText(
    schoolDetails,
    "districtName",
    "district_name",
    "district",
  );
  if (!shouldFetchPostal) {
    const state = { value: apiStateName, isLoading: false };
    const district = { value: apiDistrictName, isLoading: false };
    if (includeIds) {
      state.id = schoolDetails?.stateId ?? null;
      district.id = schoolDetails?.districtId ?? null;
    }
    return { state, district };
  }
  const postalData = postalResponse?.data;
  const isLoading = isPostalLoading || isPostalFetching;
  const state = lookupState(
    isLoading,
    isPostalError,
    apiStateName,
    postalData?.state?.name,
  );
  const district = apiDistrictName
    ? { value: apiDistrictName, isLoading: false }
    : lookupState(isLoading, isPostalError, null, postalData?.district?.name);
  if (includeIds) {
    state.id = resolvePostalId(
      isLoading,
      isPostalError,
      postalData?.state?.id,
      schoolDetails?.stateId,
    );
    district.id = apiDistrictName
      ? (schoolDetails?.districtId ?? null)
      : resolvePostalId(
          isLoading,
          isPostalError,
          postalData?.district?.id,
          schoolDetails?.districtId,
        );
  }
  return { state, district };
};

const staticLookup = (items, id, labelFn) => ({
  value: labelFn
    ? labelFn(findMappingItemById(items, id))
    : (findMappingItemById(items, id)?.displayName ?? null),
  isLoading: false,
});

const idsLookupMap = (ids, items, labelFn) =>
  Object.fromEntries(ids.map((id) => [id, staticLookup(items, id, labelFn)]));
export const buildLookupsFromMappingLists = ({
  schoolDetails,
  countryItems = [],
  cityItems = [],
  boardItems = [],
  gradeItems = [],
  tierItems = [],
  stateLookup = { value: null, isLoading: false },
  districtLookup = { value: null, isLoading: false },
}) => ({
  country: staticLookup(countryItems, schoolDetails?.countryId),
  city: staticLookup(cityItems, schoolDetails?.cityId),
  tier: staticLookup(tierItems, schoolDetails?.tierId),
  boards: idsLookupMap(
    [
      ...new Set(
        SCHOOL_BOARD_FIELDS.flatMap(
          ({ name }) => schoolDetails?.[name] || [],
        ),
      ),
    ],
    boardItems,
  ),
  grades: idsLookupMap(
    [
      ...new Set(
        (schoolDetails?.gradeWiseStudentCount || [])
          .map((row) => row.gradeId)
          .filter((id) => id != null && id !== ""),
      ),
    ],
    gradeItems,
    (item) => getMdsResourceLabel(SCHOOL_MDS_RESOURCE_TYPES.GRADE, item),
  ),
  state: stateLookup,
  district: districtLookup,
});

export const mapSchoolDetailsToFormValuesWithLookups = (
  schoolDetails,
  lookups,
  { annualSchoolFee } = {},
) => {
  if (!schoolDetails) return null;
  const isInitialIndia = isIndiaCountry(lookups?.country?.value ?? null);
  const countryId = normalizeFormId(schoolDetails.countryId);
  return {
    schoolName: schoolDetails.schoolName || "",
    udiseCode: normalizeUdiseCodeForForm(
      schoolDetails.udiseCode,
      isInitialIndia,
    ),
    schoolType: formatSchoolType(schoolDetails.schoolType),
    address: schoolDetails.address || "",
    countryId,
    cityId: optionalFormId(schoolDetails.cityId),
    state: lookups?.state?.value ?? "",
    district: isInitialIndia
      ? schoolDetails.districtName?.trim() || lookups?.district?.value || ""
      : (lookups?.district?.value ?? ""),
    stateId: isInitialIndia
      ? resolveFormId(schoolDetails.stateId, lookups?.state?.id)
      : "",
    districtId: isInitialIndia
      ? resolveFormId(schoolDetails.districtId, lookups?.district?.id)
      : "",
    area: schoolDetails.area || "",
    postalCode: schoolDetails.postalCode || "",
    ...Object.fromEntries(
      SCHOOL_BOARD_FIELDS.map(({ name }) => [
        name,
        normalizeBoardIdList(schoolDetails[name]),
      ]),
    ),
    totalStudentCount: schoolDetails.totalStudentCount ?? "",
    totalTeacherCount: schoolDetails.totalTeacherCount ?? "",
    fee: normalizeFeeForForm(annualSchoolFee),
    tierId: optionalFormId(schoolDetails.tierId),
    gradewiseStudentCount: (schoolDetails.gradeWiseStudentCount || []).map(
      (row) => ({
        gradeId: normalizeFormId(row.gradeId),
        studentCount: row.studentCount ?? "",
      }),
    ),
    pocId: schoolDetails.pocId ? normalizeFormId(schoolDetails.pocId) : "",
    executiveId: schoolDetails.executiveId
      ? normalizeFormId(schoolDetails.executiveId)
      : "",
  };
};

const toMappingOption = (item, label = item.displayName) => ({
  key: item.id,
  value: normalizeFormId(item.id),
  label,
});

const mapListToOptions = (list, { filter, label } = {}) =>
  (filter ? list.filter(filter) : list).map((item) =>
    toMappingOption(item, label ? label(item) : undefined),
  );
export const getCountryOptions = (countryList) =>
  mapListToOptions(countryList, {
    filter: (country) => isSelectableCountry(country.displayName),
  });
const getCityOptions = (cityList, countryId) =>
  cityList
    .filter((city) => !countryId || idsMatch(city.countryId, countryId))
    .map((city) => ({
      ...toMappingOption(city),
      tierId: city.tierId != null ? normalizeFormId(city.tierId) : city.tierId,
      countryId: normalizeFormId(city.countryId),
    }));
const getBoardOptions = (boardList) => mapListToOptions(boardList);
const getGradeOptions = (gradeList) =>
  mapListToOptions(gradeList, {
    label: (grade) => `${GRADE_DISPLAY_PREFIX}${grade.name}`,
  });
const getTierOptions = (tierList) => mapListToOptions(tierList);

const useMappingList = (useFetchHook, toOptions) => {
  const { data, isPending, isError, error } = useFetchHook();
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const options = useMemo(() => toOptions(items), [items, toOptions]);
  return { items, options, isPending, isError, error };
};

const mappingListExports = (name, list, optionsKey) => {
  const label = name.charAt(0).toUpperCase() + name.slice(1);
  return {
    [optionsKey]: list.options,
    [`${name}Items`]: list.items,
    [`is${label}ListLoading`]: list.isPending,
    [`isErrorOn${label}List`]: list.isError,
    [`errorMessageOnFetch${label}List`]: list.error,
  };
};

export const useSchoolMappingLists = () => {
  const country = useMappingList(useFetchCountryList, getCountryOptions);
  const city = useMappingList(useFetchCityList, getCityOptions);
  const board = useMappingList(useFetchBoardList, getBoardOptions);
  const grade = useMappingList(useFetchGradeList, getGradeOptions);
  const tier = useMappingList(useFetchTierList, getTierOptions);
  return {
    ...mappingListExports("country", country, "countryOptions"),
    ...mappingListExports("city", city, "allCityOptions"),
    ...mappingListExports("board", board, "boardOptions"),
    ...mappingListExports("grade", grade, "gradeOptions"),
    ...mappingListExports("tier", tier, "tierOptions"),
    isMappingListsLoading:
      country.isPending ||
      city.isPending ||
      board.isPending ||
      grade.isPending ||
      tier.isPending,
  };
};

export const useSchoolDetailsLookups = (
  schoolDetails,
  { includeIds = false, annualSchoolFee } = {},
) => {
  const mapping = useSchoolMappingLists();
  const postalCode = String(schoolDetails?.postalCode || "");
  const shouldFetchPostal = useMemo(() => {
    if (mapping.isCountryListLoading) return false;
    return shouldFetchSchoolPostalCode(schoolDetails, mapping.countryItems);
  }, [schoolDetails, mapping.countryItems, mapping.isCountryListLoading]);
  const {
    data: postalResponse,
    isPending: isPostalLoading,
    isFetching: isPostalFetching,
    isError: isPostalError,
  } = useFetchPostalCodeDetails(shouldFetchPostal ? postalCode : undefined);
  const stateDistrictLookups = useMemo(
    () =>
      buildSchoolStateDistrictLookups({
        schoolDetails,
        shouldFetchPostal,
        postalResponse,
        isPostalLoading,
        isPostalFetching,
        isPostalError,
        includeIds,
      }),
    [
      schoolDetails,
      shouldFetchPostal,
      postalResponse,
      isPostalLoading,
      isPostalFetching,
      isPostalError,
      includeIds,
    ],
  );
  const lookups = useMemo(
    () =>
      buildLookupsFromMappingLists({
        schoolDetails,
        countryItems: mapping.countryItems,
        cityItems: mapping.cityItems,
        boardItems: mapping.boardItems,
        gradeItems: mapping.gradeItems,
        tierItems: mapping.tierItems,
        stateLookup: stateDistrictLookups.state,
        districtLookup: stateDistrictLookups.district,
      }),
    [schoolDetails, mapping, stateDistrictLookups],
  );
  const initialValues = useMemo(
    () =>
      mapSchoolDetailsToFormValuesWithLookups(schoolDetails, lookups, {
        annualSchoolFee,
      }),
    [schoolDetails, lookups, annualSchoolFee],
  );
  const isPostalLookupLoading = shouldFetchPostal && isPostalLoading;
  return {
    ...mapping,
    lookups,
    initialValues,
    isFormReady: !mapping.isMappingListsLoading && !isPostalLookupLoading,
    isPostalLookupLoading,
  };
};

export const useSchoolAgentAutocomplete = (
  selectedAgentId,
  searchTerm,
  useSearchAgents,
  identifierField,
  assignedAgentsResponse,
) => {
  const agentNameSearchTerm = getAgentNameSearchTerm(searchTerm);
  const searchableName =
    agentNameSearchTerm.length >= MIN_POC_SEARCH_LENGTH
      ? agentNameSearchTerm
      : "";
  const { data: searchResponse, isFetching: isSearching } =
    useSearchAgents(searchableName);
  const selectedOption = useMemo(() => {
    if (!selectedAgentId) return null;
    const agent =
      getAgentByUamId(searchResponse, selectedAgentId) ||
      getAgentByUamId(assignedAgentsResponse, selectedAgentId);
    return toAgentSelectOption(agent, identifierField);
  }, [
    selectedAgentId,
    searchResponse,
    assignedAgentsResponse,
    identifierField,
  ]);
  const options = useMemo(() => {
    const searchOptions = mapAgentsToSelectOptions(
      searchResponse,
      identifierField,
    );
    if (!selectedOption) return searchOptions;
    const hasSelectedOption = searchOptions.some((option) =>
      idsMatch(option.value, selectedOption.value),
    );
    if (hasSelectedOption) return searchOptions;
    return [selectedOption, ...searchOptions];
  }, [searchResponse, selectedOption, identifierField]);
  return {
    options,
    selectedOption,
    isSearching,
    minSearchLength: MIN_POC_SEARCH_LENGTH,
    searchTerm: agentNameSearchTerm,
  };
};

export const useSchoolPocAutocomplete = (
  pocId,
  searchTerm,
  assignedAgentsResponse,
) =>
  useSchoolAgentAutocomplete(
    pocId,
    searchTerm,
    useSearchSchoolPocAgents,
    "phone",
    assignedAgentsResponse,
  );

export const useSchoolExecutiveIdAutocomplete = (
  executiveId,
  searchTerm,
  assignedAgentsResponse,
) =>
  useSchoolAgentAutocomplete(
    executiveId,
    searchTerm,
    useSearchExecutiveAgents,
    "employeeCode",
    assignedAgentsResponse,
  );
